/**
 * W65 — ForecastOverridePanel
 * Current forecast bucket, composite score, override dropdown + defense.
 * Override requires defense text >= 20 chars before save.
 */
import { useState } from "react";
import { getSession } from "@/lib/session";

const FORECAST_HEX: Record<string, string> = {
  Commit:      "#16A34A",
  Upside:      "#16A34A",
  "Best Case": "#EAB308",
  Pipeline:    "#EA580C",
  Omit:        "#DC2626",
};

const BUCKETS = ["Commit", "Upside", "Best Case", "Pipeline", "Omit"];

interface ForecastOverridePanelProps {
  opportunityId: string;
  compositeScore: number;
  forecastBucket: string;
  overrideActive: boolean;
  overrideDefense?: string | null;
  overrideSetAt?: string | null;
  overrideSetByName?: string | null;
  forecastCategoryOverride?: string | null;
  onSaved: () => void;
}

export function ForecastOverridePanel({
  opportunityId,
  compositeScore,
  forecastBucket,
  overrideActive,
  overrideDefense,
  overrideSetAt,
  overrideSetByName,
  forecastCategoryOverride,
  onSaved,
}: ForecastOverridePanelProps) {
  const [selectedOverride, setSelectedOverride] = useState<string>(
    forecastCategoryOverride ?? ""
  );
  const [defense, setDefense] = useState(overrideDefense ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const bucketColor = FORECAST_HEX[forecastBucket] ?? "#6B7280";
  const overrideColor = selectedOverride ? (FORECAST_HEX[selectedOverride] ?? "#6B7280") : null;

  async function handleSave() {
    setError(null);
    setSuccess(false);

    if (selectedOverride && defense.trim().length < 20) {
      setError("Override defense must be at least 20 characters.");
      return;
    }

    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        forecast_category_override: selectedOverride || null,
        override_defense: selectedOverride ? defense.trim() : null,
      };

      const res = await fetch(`/api/opportunities/${opportunityId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Failed to save override");
      }
      setSuccess(true);
      onSaved();
      setTimeout(() => setSuccess(false), 2500);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const scoreBarWidth = Math.max(0, Math.min(100, compositeScore));

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 8,
        border: "1px solid #E5E5E7",
        padding: "14px 16px",
      }}
    >
      <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>
        Forecast &amp; Override
      </h3>

      {/* Current bucket */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, marginBottom: 4 }}>CURRENT FORECAST</div>
        <span
          style={{
            display: "inline-flex",
            padding: "5px 12px",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 700,
            background: bucketColor,
            color: "#FFFFFF",
          }}
        >
          {forecastBucket}
        </span>
        {overrideActive && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 10,
              color: "#B45309",
              fontWeight: 600,
              background: "#FEF3C7",
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid #FCD34D",
            }}
          >
            Override active
          </span>
        )}
      </div>

      {/* Composite score bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>COMPOSITE SCORE</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#2A2A2E" }}>{Math.round(compositeScore)}</span>
        </div>
        <div style={{ height: 6, background: "#EFEFEE", borderRadius: 3, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${scoreBarWidth}%`,
              background: bucketColor,
              borderRadius: 3,
              transition: "width 0.3s",
            }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2, fontSize: 9, color: "#C9C9CC" }}>
          <span>0</span>
          <span>30</span>
          <span>50</span>
          <span>65</span>
          <span>80</span>
          <span>100</span>
        </div>
      </div>

      {/* Override section */}
      <div>
        <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, marginBottom: 6 }}>OVERRIDE BUCKET</div>
        <select
          value={selectedOverride}
          onChange={(e) => {
            setSelectedOverride(e.target.value);
            if (!e.target.value) setDefense("");
            setError(null);
          }}
          style={{
            width: "100%",
            border: "1px solid #C9C9CC",
            borderRadius: 4,
            padding: "6px 8px",
            fontSize: 13,
            marginBottom: 10,
            background: overrideColor
              ? `${overrideColor}22`
              : "#FFFFFF",
            color: overrideColor ?? "#2A2A2E",
            fontWeight: overrideColor ? 700 : 400,
          }}
        >
          <option value="">— inherit from score —</option>
          {BUCKETS.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>

        {selectedOverride && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, display: "block", marginBottom: 4 }}>
              OVERRIDE DEFENSE <span style={{ color: "#DC2626" }}>*</span>{" "}
              <span style={{ fontSize: 10, fontWeight: 400 }}>
                ({defense.trim().length}/20 chars minimum)
              </span>
            </label>
            <textarea
              value={defense}
              onChange={(e) => setDefense(e.target.value)}
              placeholder="Explain why this override is warranted. Minimum 20 characters required."
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: `1px solid ${defense.trim().length >= 20 || defense.length === 0 ? "#C9C9CC" : "#DC2626"}`,
                borderRadius: 4,
                padding: "6px 8px",
                fontSize: 12,
                lineHeight: 1.5,
                minHeight: 72,
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
          </div>
        )}

        {error && (
          <div style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 12, padding: "6px 10px", borderRadius: 4, marginBottom: 8 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: "#D1FAE5", color: "#065F46", fontSize: 12, padding: "6px 10px", borderRadius: 4, marginBottom: 8 }}>
            Forecast saved.
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: "#0B0B0F",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 5,
            padding: "7px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save Forecast"}
        </button>
      </div>

      {/* Override history */}
      {overrideActive && overrideSetAt && (
        <div
          style={{
            marginTop: 14,
            padding: "8px 10px",
            background: "#FEF3C7",
            borderRadius: 5,
            border: "1px solid #FCD34D",
            fontSize: 11,
            color: "#92400E",
          }}
        >
          Override active since{" "}
          <strong>
            {new Date(overrideSetAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}{" "}
            {new Date(overrideSetAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </strong>
          {overrideSetByName && ` by ${overrideSetByName}`}.
          <div style={{ marginTop: 4, fontStyle: "italic", color: "#78350F" }}>
            &ldquo;{overrideDefense}&rdquo;
          </div>
        </div>
      )}
    </div>
  );
}
